/**
 * SmartImprove Demo Profiles — Admin Page
 *
 * Manages reusable SmartImprove operational-improvement scenarios. This
 * collection is completely separate from SmartEvaluate's `demo_profiles`
 * (which are questionnaire-based). SmartImprove profiles are operational-
 * finding-based and ship with: company context, warehouse landscape, and
 * a list of findings keyed by Business Process / sub-process.
 *
 * UI mirrors the SmartEvaluate Demo Profiles page conventions (Search +
 * card grid + slide-out edit drawer + clone / delete actions).
 */
import { useState, useEffect, useMemo } from "react";
import {
  listSiDemos,
  createSiDemo,
  updateSiDemo,
  deleteSiDemo,
  duplicateSiDemo,
} from "../../lib/adminApi";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../../components/ui/sheet";
import {
  Pencil,
  Trash2,
  Copy,
  Star,
  Check,
  X,
  Search,
  Plus,
  Warehouse,
  AlertTriangle,
} from "lucide-react";

const COMPLEXITY_OPTIONS = ["Low", "Medium", "High", "Very High"];
const PRIORITY_OPTIONS = ["Low", "Medium", "High", "Critical"];
const IMPACT_AREAS = [
  "Efficiency",
  "Compliance",
  "Visibility",
  "Productivity",
  "Inventory Accuracy",
  "Operational Control",
  "Standardization",
  "Cost Reduction",
  "Service Level",
  "Safety",
];

const EMPTY_FORM = {
  profile_name: "",
  profile_description: "",
  industry: "",
  business_model: "",
  engagement_type: "workshop",
  strategic_objective: "",
  complexity_label: "Medium",
  warehouse_landscape: [],
  sites: [],
  findings: [],
  active: true,
  default: false,
};

const complexityStyle = (lvl) => {
  switch ((lvl || "").toLowerCase()) {
    case "low":
      return "bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]";
    case "medium":
      return "bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]";
    case "high":
      return "bg-[#FFF7ED] text-[#C2410C] border-[#FED7AA]";
    case "very high":
      return "bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]";
    default:
      return "bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]";
  }
};

export default function SiDemoProfilesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saveError, setSaveError] = useState("");
  const [msg, setMsg] = useState("");
  const [search, setSearch] = useState("");
  const [areas, setAreas] = useState([]);

  const load = () => {
    setLoading(true);
    listSiDemos()
      .then((rows) => setItems(Array.isArray(rows) ? rows : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  // Fetch active Business Processes for the finding-area dropdown.
  useEffect(() => {
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/smartimprove/operational-areas`,
      { credentials: "include" },
    )
      .then((r) => r.json())
      .then((rows) => setAreas(Array.isArray(rows) ? rows : []))
      .catch(() => setAreas([]));
  }, []);

  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditing(null);
    setSaveError("");
  };

  const openCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = (p) => {
    setForm({
      profile_name: p.profile_name || "",
      profile_description: p.profile_description || "",
      industry: p.industry || "",
      business_model: p.business_model || "",
      engagement_type: p.engagement_type || "workshop",
      strategic_objective: p.strategic_objective || "",
      complexity_label: p.complexity_label || "Medium",
      warehouse_landscape: Array.isArray(p.warehouse_landscape)
        ? p.warehouse_landscape
        : [],
      sites: Array.isArray(p.sites) ? p.sites : [],
      findings: Array.isArray(p.findings) ? p.findings : [],
      active: p.active !== false,
      default: Boolean(p.default),
    });
    setEditing(p.id);
    setSaveError("");
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    resetForm();
  };

  const flash = (text) => {
    setMsg(text);
    setTimeout(() => setMsg(""), 2200);
  };

  const save = async () => {
    if (!form.profile_name.trim()) {
      setSaveError("Profile name is required.");
      return;
    }
    setSaveError("");
    try {
      if (editing) await updateSiDemo(editing, form);
      else await createSiDemo(form);
      flash(editing ? "Profile updated." : "Profile created.");
      closeForm();
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setSaveError(typeof d === "string" ? d : "Error saving profile.");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete SmartImprove demo profile "${p.profile_name}"?`)) return;
    await deleteSiDemo(p.id);
    flash("Profile deleted.");
    load();
  };

  const duplicate = async (p) => {
    try {
      const cloned = await duplicateSiDemo(p.id);
      flash("Profile cloned.");
      await load();
      if (cloned && cloned.id) openEdit(cloned);
    } catch {
      flash("Error cloning profile.");
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter((p) =>
      [
        p.profile_name,
        p.profile_description,
        p.industry,
        p.complexity_label,
        p.strategic_objective,
      ]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(q)),
    );
  }, [items, search]);

  return (
    <div data-testid="si-demo-profiles-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-1 gap-3 flex-wrap">
        <div>
          <p className="text-[10px] font-semibold tracking-[0.18em] uppercase text-[#025979]">
            SmartImprove · Configuration
          </p>
          <h1
            className="text-xl font-bold text-[#111827] mt-0.5"
            style={{ fontFamily: "Chivo" }}
            data-testid="si-demo-profiles-title"
          >
            Demo Profiles
          </h1>
          <p className="text-xs text-[#6B7280] mt-1 max-w-2xl">
            Reusable SmartImprove operational improvement scenarios used for
            demos, training, and internal testing. Each profile bundles a
            warehouse landscape and a curated list of operational findings,
            which load into a new SmartImprove engagement as{" "}
            <strong>Accepted</strong>.
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
          data-testid="si-demo-profile-new-btn"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Profile
        </Button>
      </div>

      {msg && (
        <div
          className="my-3 px-3 py-2 bg-[#E0F2FE] border border-[#025979]/20 rounded-sm text-xs text-[#025979]"
          data-testid="si-demo-profiles-flash"
        >
          {msg}
        </div>
      )}

      {/* Access scope note */}
      <div
        className="mt-4 mb-4 rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] px-4 py-3"
        data-testid="si-demo-mode-scope-note"
      >
        <p className="text-xs font-semibold text-[#111827]">
          Who sees <em>Load Demo Profile</em>?
        </p>
        <p className="text-[11px] text-[#6B7280] mt-0.5 max-w-2xl">
          Strictly role-based. The button on the SmartImprove intake page is
          visible only to users whose role is <strong>Admin</strong> or
          <strong> Demo</strong>. Consultants, clients, and logged-out users
          never see it. Selecting a profile creates one new engagement with
          findings marked as <strong>Accepted</strong>; improvement analysis,
          recommendations, and ROI remain empty until the consultant clicks{" "}
          <strong>Generate Improvement Analysis</strong>.
        </p>
      </div>

      {/* Search */}
      <div className="mt-4 mb-4 flex items-center gap-2 max-w-md">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#9CA3AF]" />
          <Input
            placeholder="Search profiles…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 rounded-sm text-xs h-8"
            data-testid="si-demo-profiles-search"
          />
        </div>
      </div>

      {/* Edit drawer */}
      <Sheet open={showForm} onOpenChange={(o) => { if (!o) closeForm(); }}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-2xl p-0 overflow-y-auto bg-white"
          data-testid="si-demo-profile-form"
        >
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#E5E7EB] space-y-1">
            <SheetTitle
              className="text-base font-semibold text-[#111827]"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              {editing ? "Edit SmartImprove Demo Profile" : "Create New SmartImprove Demo Profile"}
            </SheetTitle>
            <SheetDescription className="text-[11px] text-[#6B7280]">
              Configure metadata, warehouse landscape, and operational
              findings. When loaded, findings are inserted with status =
              Accepted.
            </SheetDescription>
          </SheetHeader>

          <div className="px-6 py-5 space-y-5">
            <ProfileMetaSection form={form} setForm={setForm} />
            <WarehouseLandscapeSection form={form} setForm={setForm} />
            <SitesSection form={form} setForm={setForm} />
            <FindingsSection form={form} setForm={setForm} areas={areas} />

            {saveError && (
              <p
                className="text-[11px] text-red-600 mt-3"
                data-testid="si-demo-profile-error"
              >
                {saveError}
              </p>
            )}
          </div>

          <div className="sticky bottom-0 left-0 right-0 bg-white border-t border-[#E5E7EB] px-6 py-3 flex items-center justify-end gap-2">
            <Button
              variant="outline"
              onClick={closeForm}
              className="rounded-sm text-xs"
              data-testid="si-demo-profile-cancel-btn"
            >
              <X className="w-3 h-3 mr-1" /> Cancel
            </Button>
            <Button
              onClick={save}
              className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
              data-testid="si-demo-profile-save-btn"
            >
              <Check className="w-3 h-3 mr-1" /> Save Profile
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Profile Cards */}
      {loading ? (
        <div className="px-4 py-12 text-center text-xs text-[#9CA3AF]">
          Loading profiles…
        </div>
      ) : filtered.length === 0 ? (
        <div
          className="bg-white border border-dashed border-[#E5E7EB] rounded-sm px-4 py-12 text-center"
          data-testid="si-demo-profiles-empty"
        >
          <p className="text-sm text-[#6B7280]">
            {items.length === 0
              ? "No SmartImprove demo profiles yet. Click Add Profile to create one."
              : "No profiles match your search."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((p) => (
            <div
              key={p.id}
              className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm p-4 flex flex-col"
              data-testid={`si-demo-profile-card-${p.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-semibold text-[#111827] truncate">
                      {p.profile_name}
                    </h3>
                    {p.default && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A] text-[10px]">
                        <Star className="w-2.5 h-2.5" fill="currentColor" />
                        Default
                      </span>
                    )}
                    {!p.active && (
                      <span className="px-1.5 py-0.5 rounded-full bg-[#F3F4F6] text-[#6B7280] border border-[#E5E7EB] text-[10px]">
                        Inactive
                      </span>
                    )}
                  </div>
                  {p.profile_description && (
                    <p className="text-[11px] text-[#6B7280] mt-1 line-clamp-2">
                      {p.profile_description}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-3 flex flex-wrap gap-1.5">
                {p.industry && (
                  <span className="px-2 py-0.5 rounded-full border text-[10px] bg-[#E6F4FA] text-[#025979] border-[#B5DEF0]">
                    {p.industry}
                  </span>
                )}
                {p.complexity_label && (
                  <span
                    className={`px-2 py-0.5 rounded-full border text-[10px] ${complexityStyle(p.complexity_label)}`}
                  >
                    {p.complexity_label} Complexity
                  </span>
                )}
              </div>

              <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                <dt className="text-[#6B7280]">Warehouses</dt>
                <dd className="text-[#111827] text-right">
                  {(p.sites || []).length}
                </dd>
                <dt className="text-[#6B7280]">Findings</dt>
                <dd className="text-[#111827] text-right">
                  {(p.findings || []).length}
                </dd>
                {p.strategic_objective && (
                  <>
                    <dt className="text-[#6B7280]">Objective</dt>
                    <dd className="text-[#111827] text-right truncate">
                      {p.strategic_objective}
                    </dd>
                  </>
                )}
              </dl>

              <div className="mt-4 pt-3 border-t border-[#F3F4F6] flex items-center justify-between gap-2">
                <button
                  onClick={() => duplicate(p)}
                  title="Clone this profile and modify it."
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm text-xs font-medium text-[#025979] border border-[#025979]/25 bg-white hover:bg-[#E0F2FE] transition-colors"
                  data-testid={`si-demo-profile-clone-${p.id}`}
                >
                  <Copy className="w-3 h-3" />
                  Clone Profile
                </button>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => openEdit(p)}
                    title="Edit"
                    className="p-1.5 rounded-sm text-[#6B7280] hover:bg-[#F3F4F6] hover:text-[#025979]"
                    data-testid={`si-demo-profile-edit-${p.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => remove(p)}
                    title="Delete"
                    className="p-1.5 rounded-sm text-[#6B7280] hover:bg-[#FEF2F2] hover:text-[#B91C1C]"
                    data-testid={`si-demo-profile-delete-${p.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Form sections
// ---------------------------------------------------------------------------
function ProfileMetaSection({ form, setForm }) {
  return (
    <div>
      <h4 className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#6B7280] mb-2">
        Profile Metadata
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <label className="text-[11px] text-[#6B7280] font-medium">
            Profile Name <span className="text-red-500">*</span>
          </label>
          <Input
            value={form.profile_name}
            onChange={(e) => setForm({ ...form, profile_name: e.target.value })}
            placeholder="e.g. Pharma Warehouse Improvement Benchmark"
            className="rounded-sm text-xs mt-1"
            data-testid="si-demo-profile-name-input"
          />
        </div>
        <div className="md:col-span-2">
          <label className="text-[11px] text-[#6B7280] font-medium">
            Description
          </label>
          <Textarea
            value={form.profile_description}
            onChange={(e) =>
              setForm({ ...form, profile_description: e.target.value })
            }
            placeholder="Short summary shown on the profile card."
            className="rounded-sm text-xs mt-1 min-h-[60px]"
            data-testid="si-demo-profile-description-input"
          />
        </div>
        <div>
          <label className="text-[11px] text-[#6B7280] font-medium">
            Industry
          </label>
          <Input
            value={form.industry}
            onChange={(e) => setForm({ ...form, industry: e.target.value })}
            placeholder="e.g. Pharma / Life Sciences"
            className="rounded-sm text-xs mt-1"
          />
        </div>
        <div>
          <label className="text-[11px] text-[#6B7280] font-medium">
            Complexity
          </label>
          <Select
            value={form.complexity_label}
            onValueChange={(v) => setForm({ ...form, complexity_label: v })}
          >
            <SelectTrigger className="rounded-sm text-xs mt-1 h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COMPLEXITY_OPTIONS.map((o) => (
                <SelectItem key={o} value={o}>
                  {o}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[11px] text-[#6B7280] font-medium">
            Business Model
          </label>
          <Input
            value={form.business_model}
            onChange={(e) =>
              setForm({ ...form, business_model: e.target.value })
            }
            placeholder="e.g. b2b, b2c, both"
            className="rounded-sm text-xs mt-1"
          />
        </div>
        <div>
          <label className="text-[11px] text-[#6B7280] font-medium">
            Engagement Type
          </label>
          <Select
            value={form.engagement_type}
            onValueChange={(v) => setForm({ ...form, engagement_type: v })}
          >
            <SelectTrigger className="rounded-sm text-xs mt-1 h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="workshop">Workshop</SelectItem>
              <SelectItem value="remote_review">Remote Review</SelectItem>
              <SelectItem value="post_go_live">Post Go-Live</SelectItem>
              <SelectItem value="optimization">Optimization</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <label className="text-[11px] text-[#6B7280] font-medium">
            Strategic Objective
          </label>
          <Input
            value={form.strategic_objective}
            onChange={(e) =>
              setForm({ ...form, strategic_objective: e.target.value })
            }
            placeholder="e.g. operational_excellence"
            className="rounded-sm text-xs mt-1"
          />
        </div>
        <div className="md:col-span-2 flex items-center gap-4 pt-1">
          <label className="text-xs flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
              data-testid="si-demo-profile-active-checkbox"
            />
            Active
          </label>
          <label className="text-xs flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={form.default}
              onChange={(e) => setForm({ ...form, default: e.target.checked })}
              data-testid="si-demo-profile-default-checkbox"
            />
            Default
          </label>
        </div>
      </div>
    </div>
  );
}

function WarehouseLandscapeSection({ form, setForm }) {
  const [draft, setDraft] = useState("");
  const items = form.warehouse_landscape || [];
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    setForm({ ...form, warehouse_landscape: [...items, v] });
    setDraft("");
  };
  const remove = (idx) => {
    setForm({
      ...form,
      warehouse_landscape: items.filter((_, i) => i !== idx),
    });
  };
  return (
    <div>
      <h4 className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#6B7280] mb-2">
        Warehouse Landscape
      </h4>
      <p className="text-[11px] text-[#6B7280] mb-2">
        Short bullet descriptors. Shown on profile cards & in the selector
        modal (e.g. <em>2 Manufacturing Plants</em>, <em>SAP EWM Decentralized</em>).
      </p>
      <div className="flex items-center gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder="Add bullet (Enter to add)"
          className="rounded-sm text-xs"
        />
        <Button
          type="button"
          onClick={add}
          className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs h-9 px-3"
        >
          Add
        </Button>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {items.length === 0 ? (
          <p className="text-[11px] text-[#9CA3AF] italic">No bullets yet.</p>
        ) : (
          items.map((b, idx) => (
            <span
              key={idx}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] bg-[#F9FAFB] text-[#374151] border-[#E5E7EB]"
            >
              {b}
              <button
                type="button"
                onClick={() => remove(idx)}
                className="text-[#9CA3AF] hover:text-[#B91C1C]"
              >
                <X className="w-2.5 h-2.5" />
              </button>
            </span>
          ))
        )}
      </div>
    </div>
  );
}

function SitesSection({ form, setForm }) {
  const sites = form.sites || [];
  const add = () => {
    setForm({
      ...form,
      sites: [
        ...sites,
        {
          site_code: "",
          warehouse_name: "",
          location_city: "",
          country: "",
          warehouse_type: "",
          sap_landscape: "",
          ewm_version: "",
          site_size: "",
          automation_maturity: "",
          notes: "",
        },
      ],
    });
  };
  const remove = (idx) => {
    setForm({ ...form, sites: sites.filter((_, i) => i !== idx) });
  };
  const patch = (idx, key, value) => {
    setForm({
      ...form,
      sites: sites.map((s, i) => (i === idx ? { ...s, [key]: value } : s)),
    });
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#6B7280] inline-flex items-center gap-1.5">
          <Warehouse className="w-3 h-3" /> Warehouse Sites
        </h4>
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-1 text-[11px] text-[#025979] hover:underline"
        >
          <Plus className="w-3 h-3" /> Add Site
        </button>
      </div>
      {sites.length === 0 ? (
        <p className="text-[11px] text-[#9CA3AF] italic">
          No sites yet — Add Site to build the warehouse landscape.
        </p>
      ) : (
        <div className="space-y-3">
          {sites.map((s, idx) => (
            <div
              key={idx}
              className="border border-[#E5E7EB] rounded-sm p-3 bg-[#F8FAFC]"
            >
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] font-semibold text-[#111827]">
                  Site {idx + 1}
                </p>
                <button
                  type="button"
                  onClick={() => remove(idx)}
                  className="text-[#9CA3AF] hover:text-[#B91C1C]"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={s.warehouse_name}
                  onChange={(e) => patch(idx, "warehouse_name", e.target.value)}
                  placeholder="Warehouse name"
                  className="rounded-sm text-[11px] h-8"
                />
                <Input
                  value={s.site_code}
                  onChange={(e) => patch(idx, "site_code", e.target.value)}
                  placeholder="Site code (e.g. PHR-MFG-01)"
                  className="rounded-sm text-[11px] h-8"
                />
                <Input
                  value={s.location_city}
                  onChange={(e) => patch(idx, "location_city", e.target.value)}
                  placeholder="City"
                  className="rounded-sm text-[11px] h-8"
                />
                <Input
                  value={s.country}
                  onChange={(e) => patch(idx, "country", e.target.value)}
                  placeholder="Country"
                  className="rounded-sm text-[11px] h-8"
                />
                <Input
                  value={s.warehouse_type}
                  onChange={(e) => patch(idx, "warehouse_type", e.target.value)}
                  placeholder="Type (cdc, rdc, manufacturing, …)"
                  className="rounded-sm text-[11px] h-8"
                />
                <Input
                  value={s.sap_landscape}
                  onChange={(e) => patch(idx, "sap_landscape", e.target.value)}
                  placeholder="SAP landscape"
                  className="rounded-sm text-[11px] h-8"
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FindingsSection({ form, setForm, areas }) {
  const findings = form.findings || [];
  const add = () => {
    setForm({
      ...form,
      findings: [
        ...findings,
        {
          operational_area: "",
          sub_process: "",
          title: "",
          description: "",
          priority: "Medium",
          impact_area: "",
        },
      ],
    });
  };
  const remove = (idx) => {
    setForm({ ...form, findings: findings.filter((_, i) => i !== idx) });
  };
  const patch = (idx, key, value) => {
    setForm({
      ...form,
      findings: findings.map((f, i) => (i === idx ? { ...f, [key]: value } : f)),
    });
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#6B7280] inline-flex items-center gap-1.5">
          <AlertTriangle className="w-3 h-3" /> Operational Findings
        </h4>
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-1 text-[11px] text-[#025979] hover:underline"
        >
          <Plus className="w-3 h-3" /> Add Finding
        </button>
      </div>
      {findings.length === 0 ? (
        <p className="text-[11px] text-[#9CA3AF] italic">
          No findings yet — Add Finding to build the operational scenario.
        </p>
      ) : (
        <div className="space-y-3">
          {findings.map((f, idx) => (
            <div
              key={idx}
              className="border border-[#E5E7EB] rounded-sm p-3 bg-[#F8FAFC]"
            >
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] font-semibold text-[#111827]">
                  Finding {idx + 1}
                </p>
                <button
                  type="button"
                  onClick={() => remove(idx)}
                  className="text-[#9CA3AF] hover:text-[#B91C1C]"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-[#6B7280]">
                    Business Process
                  </label>
                  <Select
                    value={f.operational_area}
                    onValueChange={(v) => patch(idx, "operational_area", v)}
                  >
                    <SelectTrigger className="rounded-sm text-[11px] h-8 mt-0.5">
                      <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent>
                      {areas.map((a) => (
                        <SelectItem key={a.key} value={a.key}>
                          {a.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] text-[#6B7280]">Sub-process</label>
                  <Input
                    value={f.sub_process}
                    onChange={(e) => patch(idx, "sub_process", e.target.value)}
                    placeholder="e.g. Wave Management"
                    className="rounded-sm text-[11px] h-8 mt-0.5"
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] text-[#6B7280]">
                    Finding Title
                  </label>
                  <Input
                    value={f.title}
                    onChange={(e) => patch(idx, "title", e.target.value)}
                    placeholder="Short, action-oriented finding."
                    className="rounded-sm text-[11px] h-8 mt-0.5"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-[#6B7280]">Priority</label>
                  <Select
                    value={f.priority}
                    onValueChange={(v) => patch(idx, "priority", v)}
                  >
                    <SelectTrigger className="rounded-sm text-[11px] h-8 mt-0.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITY_OPTIONS.map((p) => (
                        <SelectItem key={p} value={p}>
                          {p}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] text-[#6B7280]">
                    Impact Area
                  </label>
                  <Select
                    value={f.impact_area}
                    onValueChange={(v) => patch(idx, "impact_area", v)}
                  >
                    <SelectTrigger className="rounded-sm text-[11px] h-8 mt-0.5">
                      <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent>
                      {IMPACT_AREAS.map((p) => (
                        <SelectItem key={p} value={p}>
                          {p}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
